import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useAuthContext } from 'shared/components/AuthContext'
import {
  getSuperAdminSessionExpiresAt,
  ItemTag,
  resetViewerMode,
  useViewerMode,
  ViewerMode,
} from 'shared/utils'

// Longer delays overflow and make setTimeout fire immediately
const MAX_TIMEOUT_DELAY_MS = 2 ** 31 - 1

export const useSuperAdminSessionGuard = () => {
  const { user, auth } = useAuthContext()
  const { mode } = useViewerMode()
  const { t } = useTranslation('common', { useSuspense: false })
  const { enqueueSnackbar } = useSnackbar()
  const message = t('viewerMode.sessionExpired')
  const enqueueSnackbarRef = useRef(enqueueSnackbar)
  const messageRef = useRef(message)

  enqueueSnackbarRef.current = enqueueSnackbar
  messageRef.current = message

  useEffect(() => {
    if (mode !== ViewerMode.SYSTEM_ADMIN || !user) return

    let cancelled = false
    let isLoadingSessionExpiry = false
    let sessionExpiresAt: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const logout = () => {
      if (cancelled) return
      cancelled = true
      enqueueSnackbarRef.current(messageRef.current, {
        variant: 'error',
        autoHideDuration: 8000,
      })
      auth?.signOut()
      resetViewerMode()
      localStorage.removeItem(ItemTag.CUSTOM_CREDENTIALS_HISTORY)
    }

    const scheduleLogout = (expiresAt: number) => {
      if (timeoutId) clearTimeout(timeoutId)

      const remaining = expiresAt - Date.now()
      if (remaining <= 0) {
        logout()
        return
      }

      timeoutId = setTimeout(
        checkSessionExpiry,
        Math.min(remaining, MAX_TIMEOUT_DELAY_MS)
      )
    }

    const loadSessionExpiry = () => {
      if (isLoadingSessionExpiry) return
      isLoadingSessionExpiry = true

      user
        .getIdTokenResult()
        .then(({ authTime }) => {
          const expiresAt = getSuperAdminSessionExpiresAt(authTime)
          if (cancelled || expiresAt === undefined) return

          sessionExpiresAt = expiresAt
          scheduleLogout(expiresAt)
        })
        // Retried on the next focus / visibilitychange
        .catch(() => undefined)
        .finally(() => {
          isLoadingSessionExpiry = false
        })
    }

    // setTimeout is paused while the system sleeps, so the expiry is rechecked
    // whenever the user comes back to the tab
    const checkSessionExpiry = () => {
      if (cancelled) return

      if (sessionExpiresAt === undefined) {
        loadSessionExpiry()
        return
      }

      scheduleLogout(sessionExpiresAt)
    }

    const checkSessionExpiryWhenVisible = () => {
      if (document.visibilityState === 'visible') checkSessionExpiry()
    }

    checkSessionExpiry()
    window.addEventListener('focus', checkSessionExpiry)
    document.addEventListener('visibilitychange', checkSessionExpiryWhenVisible)

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      window.removeEventListener('focus', checkSessionExpiry)
      document.removeEventListener(
        'visibilitychange',
        checkSessionExpiryWhenVisible
      )
    }
  }, [auth, mode, user])
}
